import React from "react";
import DestinationTemplate from "./DestinationTemplate";
import { MapPin, Utensils, Star, Camera } from "lucide-react";

const Indore = () => {
  const destinationData = {
    cityName: "Indore",
    stateName: "Madhya Pradesh",
    heroTitle: "Best Places to Visit in",
    heroSubtitle: "Discover the commercial capital of Madhya Pradesh - a perfect blend of history, culture, and modern lifestyle with amazing food culture and architectural marvels.",
    metaDescription: "Explore the best places to visit in Indore, Madhya Pradesh with Aaoghumen. Discover top attractions, local cuisine, travel tips and plan your perfect trip to Indore with our expert guides.",
    keywords: "indore travel guide, places to visit in indore, indore tourism, indore attractions, madhya pradesh travel, aaoghumen indore, indore food tour, indore temples, holkar palace, sarafa bazaar",
    bestTimeToVisit: "Oct - Mar",
    slug: "indore",
    
    attractions: [
      {
        name: "Rajwada Palace",
        description: "Historic palace of the Holkar dynasty showcasing Maratha architecture with beautiful wooden balconies and intricate carvings.",
        rating: 4.5,
        category: "Heritage",
        path: "/destinations/indore/rajwada-palace",
        image: "/Indore/historical/rajwada.jpg"
      },
      {
        name: "Lal Bagh Palace",
        description: "Magnificent 19th-century palace with European architecture, beautiful gardens, and a collection of royal artifacts.",
        rating: 4.6,
        category: "Palace",
        path: "/destinations/indore/lal-bagh-palace",
        image: "/Indore/historical/lalBaghpalace0.jpg"
      },
      {
        name: "Kanch Mandir",
        description: "Stunning Jain temple made entirely of glass and mirrors, creating a mesmerizing visual experience with intricate glasswork.",
        rating: 4.4,
        category: "Temple",
        path: "/destinations/indore/kanch-mandir",
        image: "/Indore/temples/kanch0.jpg"
      },
      {
        name: "Sarafa Bazaar",
        description: "Famous night food market offering the best of Indori street food, from poha jalebi to kulfi and chaat varieties.",
        rating: 4.7,
        category: "Food Market",
        path: "/destinations/indore/sarafa-bazaar",
        image: "/Indore/eating/sarafa0.jpg"
      },
      {
        name: "Khajrana Ganesh Temple",
        description: "One of the most revered Ganesh temples in India, known for fulfilling wishes and attracting devotees from across the country.",
        rating: 4.8,
        category: "Temple",
        path: "#",
        image: "/Indore/temples/khajrana0.jpg"
      },
      {
        name: "Central Museum",
        description: "Rich collection of sculptures, coins, inscriptions and artifacts showcasing the cultural heritage of Madhya Pradesh.",
        rating: 4.2,
        category: "Museum",
        path: "/destinations/indore/central-museum",
        image: "/Indore/historical/centralmuseum0.jpg"
      }
    ],

    experiences: [
      {
        icon: <Utensils className="w-6 h-6" />,
        title: "Indori Food Trail",
        description: "Experience the legendary food culture of Indore with local specialties like poha jalebi, dal bafla, and street food at Sarafa Bazaar.",
        duration: "4-5 hours"
      },
      {
        icon: <Camera className="w-6 h-6" />,
        title: "Heritage Walk",
        description: "Explore the rich Maratha heritage through palaces, temples, and historical monuments with expert local guides.",
        duration: "Full day"
      },
      {
        icon: <Star className="w-6 h-6" />,
        title: "Temple Hopping",
        description: "Visit the spiritual side of Indore with tours to Khajrana Ganesh, Kanch Mandir, and other significant temples.",
        duration: "Half day"
      },
      {
        icon: <MapPin className="w-6 h-6" />,
        title: "Shopping Experience",
        description: "Explore local markets for traditional textiles, handicrafts, and authentic Indori specialties and souvenirs.",
        duration: "3-4 hours"
      }
    ],

    howToReach: [
      {
        mode: "By Air",
        details: "Devi Ahilya Bai Holkar Airport connects Indore to major Indian cities. It's about 8 km from city center."
      },
      {
        mode: "By Train", 
        details: "Indore Junction is well-connected to major cities. Direct trains from Delhi, Mumbai, Chennai, and Kolkata."
      },
      {
        mode: "By Road",
        details: "National Highway 3 connects Indore to Mumbai (580 km) and Agra (430 km). Good bus services available."
      }
    ],

    localCuisine: [
      {
        name: "Poha Jalebi",
        description: "The iconic Indori breakfast combination - flattened rice with crispy sweet jalebis, perfect with morning tea."
      },
      {
        name: "Dal Bafla",
        description: "Traditional wheat balls served with spicy dal and ghee, similar to dal baati but with unique Indori preparation."
      },
      {
        name: "Bhutte ka Kees",
        description: "Grated corn cooked with milk and spices, a popular monsoon delicacy that's creamy and flavorful."
      },
      {
        name: "Garadu",
        description: "Deep-fried sweet potato wedges served with spicy chutneys, a favorite winter street food snack."
      }
    ],

    travelTips: [
      {
        title: "Best Time to Visit",
        description: "October to March offers pleasant weather. Avoid summer months (April-June) due to extreme heat."
      },
      {
        title: "Local Transport",
        description: "City buses, auto-rickshaws, and app-based cabs are readily available. Walking is ideal for exploring markets."
      },
      {
        title: "Food Safety",
        description: "Try street food from crowded stalls. Carry hand sanitizer and stay hydrated while food hopping."
      },
      {
        title: "Shopping Tips",
        description: "Visit Sarafa Bazaar for jewelry, MT Cloth Market for textiles. Bargaining is expected in local markets."
      }
    ],

    nearbyPlaces: [
      {
        name: "Ujjain",
        distance: "55 km",
        rating: "4.6",
        slug: "ujjain"
      },
      {
        name: "Maheshwar",
        distance: "90 km", 
        rating: "4.4",
        slug: "maheshwar"
      },
      {
        name: "Omkareshwar",
        distance: "77 km",
        rating: "4.5",
        slug: "omkareshwar"
      }
    ],

    seoContent: `Indore, the commercial capital of Madhya Pradesh, is a vibrant city that perfectly blends tradition with modernity. Known as the food capital of India, Indore offers an incredible culinary journey from street food at Sarafa Bazaar to traditional Indori specialties. The city's rich Maratha heritage is evident in magnificent palaces like Rajwada and Lal Bagh Palace, while spiritual sites like Khajrana Ganesh Temple attract millions of devotees. With Aaoghumen's expert local guides, explore hidden gems, taste authentic flavors, and experience the warm hospitality that makes Indore special. Whether you're interested in history, spirituality, food, or shopping, Indore provides an unforgettable travel experience in the heart of India.`
  };

  return <DestinationTemplate {...destinationData} />;
};

export default Indore;